import React, { useState, useEffect, Fragment, useMemo, useRef } from 'react';
import { Button, Typography, Box, CircularProgress, Grid, Stepper, Step, StepLabel, TextField } from '@mui/material';
import { ArrowBack, ArrowForward, Person, School, Settings } from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
    AGE_GROUP,
    ageGroupOptions,
    DAILY_COMMITMENT,
    dailyCommitmentOptions,
    ENGLISH_EXPERIENCE,
    englishExperienceOptions,
    LANGUAGE,
    languageOptions,
    LEARNING_GOAL,
    LEARNING_STYLE,
    learningGoalOptions,
    learningStyleOptions,
    LEVEL,
    levelOptions,
    steps,
} from '../constants';
import slice from 'lodash/slice';
import filter from 'lodash/filter';
import findIndex from 'lodash/findIndex';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { surveySchema } from '../validation';
import TextFieldCustom from '@/components/base/TextField';
import SingleSelect from '@/components/base/SingleSelect';
import { fetchCreateSurveyRequest } from '@/store/slices/testingSlice';
import CheckboxLabel from '@/components/base/CheckboxLabel';
import { completeProfileRequest } from '@/store/slices/authSlice';
import dayjs from '@/plugin/dayjs';
import { FORMAT_DATE } from '@/plugin/dayjs/constants';
import { useTranslation } from 'react-i18next';
import NumberPhone from '@/components/base/NumberPhone';
import { DatePicker } from '@mui/x-date-pickers';

const SurveyUser: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { t } = useTranslation();

    const { loadingState: isLoadingTesting, survey } = useAppSelector((state) => state.testing);
    const { user, isLoading: loadingAuth, error: authError } = useAppSelector((state) => state.auth);

    const isLoading = useMemo(() => {
        return isLoadingTesting.isLoading || loadingAuth;
    }, [isLoadingTesting.isLoading, loadingAuth]);

    const {
        handleSubmit,
        control,
        trigger,
        setValue,
        setError,
        watch,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(surveySchema()),
        defaultValues: {
            age_group: AGE_GROUP.Teen,
            native_language: LANGUAGE.VI,
            target_language: LANGUAGE.EN,
            current_level: LEVEL.A1,
            target_level: LEVEL.B2,
            english_experience: ENGLISH_EXPERIENCE.Beginner,
            daily_commitment: DAILY_COMMITMENT.Casual,
            learning_goal: LEARNING_GOAL.Ielts,
            learning_style: LEARNING_STYLE.Visual,
            study_reminders: true,
            achievement_notifications: true,
        },
    });
    const bioValue = watch('bio', '');
    const [dob] = watch(['date_of_birth']);
    const [activeStep, setActiveStep] = useState(0);

    // Go to the test only when this form created the survey. A survey left in the store from an
    // earlier visit must not redirect: when starting the test fails the page navigates back here,
    // and redirecting again would retry the start forever.
    const initialSurvey = useRef(survey);
    useEffect(() => {
        if (user) return;
        if (survey && survey !== initialSurvey.current) {
            navigate('/testing');
        }
    }, [survey, navigate, user]);

    useEffect(() => {
        // The phone number belongs to another account: send the user back to the step that has it
        if (authError === 'ERROR_PHONE_NUMBER_ALREADY_EXISTS') {
            setActiveStep(0);
            setError('phone_number', { message: t('survey.form.phoneNumber.exists') });
        }
    }, [authError]);

    useEffect(() => {
        if (dob) {
            const age = Math.abs(dayjs().diff(dayjs(dob, 'DD/MM/YYYY'), 'year'));
            handleChangeAgeGroup(age);
            trigger(['date_of_birth']);
        }
    }, [dob]);

    useEffect(() => {
        if (user) {
            setValue('email', user.email);
            setValue('target_level', LEVEL.B2);
            setValue('current_level', LEVEL.A1);
        }
    }, []);

    const validateStep = async (step: number) => {
        switch (step) {
            case 0:
                return await trigger(['email', 'date_of_birth', 'phone_number', 'bio']);
            case 1:
                return await trigger([
                    'native_language',
                    'target_language',
                    'current_level',
                    'target_level',
                    'english_experience',
                    'daily_commitment',
                ]);
            case 2:
                return await trigger([
                    'learning_goal',
                    'learning_style',
                    'study_reminders',
                    'achievement_notifications',
                ]);
        }
        return false;
    };

    const handleNext = async () => {
        const hasNextStep = await validateStep(activeStep);
        if (hasNextStep) {
            setActiveStep((prev) => prev + 1);
        }
    };

    const handleBack = () => {
        setActiveStep((prev) => prev - 1);
    };

    const submit = () => {
        if (isLoading) return;
        handleSubmit(
            async (data) => {
                try {
                    if (user) {
                        //TO DO: Update user profile with survey data
                        const formattedData = {
                            phone_number: data?.phone_number || '',
                            date_of_birth: dayjs(data.date_of_birth, 'DD/MM/YYYY').format(FORMAT_DATE.yyyymmdd),
                            age_group: data.age_group as AGE_GROUP,
                            bio: data.bio,
                            native_language: data.native_language,
                            target_language: data.target_language,
                            current_level: data.current_level as LEVEL,
                            target_level: data.target_level as LEVEL,
                            english_experience: data.english_experience as ENGLISH_EXPERIENCE,
                            daily_commitment: data.daily_commitment as DAILY_COMMITMENT,
                            learning_goal: data.learning_goal,
                            learning_style: data.learning_style,
                            study_reminders: data.study_reminders || false,
                            achievement_notifications: data.achievement_notifications || false,
                        };
                        await dispatch(completeProfileRequest(formattedData));
                    } else {
                        const formattedData = {
                            email: data.email,
                            phone_number: data?.phone_number || undefined,
                            date_of_birth: dayjs(data.date_of_birth, 'DD/MM/YYYY').format(FORMAT_DATE.yyyymmdd),
                            age_group: data.age_group as AGE_GROUP,
                            native_language: data.native_language,
                            target_language: data.target_language,
                            current_level: data.current_level as LEVEL,
                            target_level: data.target_level as LEVEL,
                            english_experience: data.english_experience as ENGLISH_EXPERIENCE,
                            daily_commitment: data.daily_commitment as DAILY_COMMITMENT,
                            learning_goal: data.learning_goal,
                            learning_style: data.learning_style,
                        };
                        await dispatch(fetchCreateSurveyRequest(formattedData));
                    }
                } catch (error) {
                    console.error('Profile completion failed:', error);
                }
            },
            (errors) => {
                console.log('errors:', errors);
            },
        )();
    };

    const handleChangeAgeGroup = (age: number) => {
        if (age < 13) {
            return setValue('age_group', AGE_GROUP.Child);
        }
        if (age < 20) {
            return setValue('age_group', AGE_GROUP.Teen);
        }
        if (age < 65) {
            return setValue('age_group', AGE_GROUP.Adult);
        }

        return setValue('age_group', AGE_GROUP.Senior);
    };

    const renderStepContent = (step: number) => {
        switch (step) {
            case 0:
                return (
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Typography
                                variant="h6"
                                gutterBottom
                                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                            >
                                <Person /> {t('survey.titleStep1')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                {t('survey.descriptionStep1')}
                            </Typography>
                        </Grid>
                        {!user ? (
                            <>
                                <Grid item xs={12} sm={6}>
                                    <TextFieldCustom
                                        fullWidth
                                        name="email"
                                        label={t('survey.form.email.label')}
                                        control={control}
                                        errors={errors}
                                        required
                                        placeholder={t('survey.form.email.placeholder')}
                                        disabled={!!user || isLoading}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <NumberPhone
                                        fullWidth
                                        required
                                        name="phone_number"
                                        label={t('survey.form.phoneNumber.label')}
                                        control={control}
                                        errors={errors}
                                        disabled={isLoading}
                                        placeholder={t('survey.form.phoneNumber.placeholder')}
                                        helperText={
                                            errors?.phone_number?.message
                                                ? undefined
                                                : t('survey.form.phoneNumber.guestHelper')
                                        }
                                        grouping={watch('phone_number')?.startsWith('84') ? [2, 3, 3, 3] : [4, 3, 3]}
                                    />
                                </Grid>
                            </>
                        ) : (
                            <Grid item xs={12} sm={6}>
                                <NumberPhone
                                    fullWidth
                                    name="phone_number"
                                    label={t('survey.form.phoneNumber.label')}
                                    control={control}
                                    errors={errors}
                                    disabled={isLoading}
                                    placeholder={t('survey.form.phoneNumber.placeholder')}
                                    grouping={watch('phone_number')?.startsWith('84') ? [2, 3, 3, 3] : [4, 3, 3]}
                                />
                            </Grid>
                        )}
                        <Grid item xs={12} sm={6}>
                            <DatePicker
                                onChange={(value) => {
                                    if (value) {
                                        handleChangeAgeGroup(dayjs(value).diff(dayjs(), 'year'));
                                        setValue('date_of_birth', value.format('DD/MM/YYYY'));
                                    }
                                }}
                                value={
                                    watch('date_of_birth') === undefined
                                        ? null
                                        : dayjs(watch('date_of_birth'), 'DD/MM/YYYY').format('MM/DD/YYYY')
                                }
                                renderInput={(params) => (
                                    //@ts-expect-error params have many fields type imcompatible
                                    <TextFieldCustom
                                        {...params}
                                        fullWidth
                                        name="date_of_birth"
                                        control={control}
                                        errors={errors}
                                        label={t('survey.form.dateOfBirth.label')}
                                        disabled={isLoading}
                                        onChange={(e) => {
                                            let value = e.target.value;
                                            if (value.length === 11) {
                                                value = slice(value, 0, -1).join('');
                                            }
                                            setValue('date_of_birth', value);
                                            return value;
                                        }}
                                        inputProps={{
                                            readOnly: true,
                                        }}
                                        placeholder="DD/MM/YYYY"
                                    />
                                )}
                                disabled={isLoading}
                                label={t('survey.form.dateOfBirth.label')}
                                className="full-width"
                                maxDate={dayjs().subtract(1, 'day')}
                                dayOfWeekFormatter={(day) => `${day}`}
                                inputFormat="DD/MM/YYYY"
                            />
                        </Grid>
                        <Grid item xs={12} sm={12}>
                            <SingleSelect
                                fullWidth
                                name="age_group"
                                control={control}
                                errors={errors}
                                label={t('survey.form.ageGroup.label')}
                                options={ageGroupOptions()}
                                defaultValue={AGE_GROUP.Teen}
                                disabled={true}
                            />
                        </Grid>
                        {user && (
                            <Grid item xs={12}>
                                <TextFieldCustom
                                    fullWidth
                                    multiline
                                    rows={3}
                                    name="bio"
                                    control={control}
                                    errors={errors}
                                    label={t('survey.form.bio.label')}
                                    disabled={isLoading}
                                    placeholder={t('survey.form.bio.placeholder')}
                                    helperText={`${bioValue?.length || 0}/500 characters`}
                                    inputProps={{ maxLength: 500 }}
                                />
                            </Grid>
                        )}
                    </Grid>
                );

            case 1:
                return (
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Typography
                                variant="h6"
                                gutterBottom
                                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                            >
                                <School /> {t('survey.titleStep2')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                {t('survey.descriptionStep2')}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <SingleSelect
                                fullWidth
                                name="native_language"
                                control={control}
                                errors={errors}
                                label={t('survey.form.nativeLanguage.label')}
                                options={languageOptions()}
                                defaultValue={LANGUAGE.VI}
                                disabled={isLoading}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <SingleSelect
                                fullWidth
                                name="target_language"
                                control={control}
                                errors={errors}
                                label={t('survey.form.targetLanguage.label')}
                                options={languageOptions()}
                                defaultValue={LANGUAGE.EN}
                                disabled={isLoading}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <SingleSelect
                                fullWidth
                                name="current_level"
                                control={control}
                                errors={errors}
                                label={t('survey.form.currentLevel.label')}
                                options={filter(
                                    levelOptions,
                                    (_, index) =>
                                        index <=
                                        findIndex(levelOptions, {
                                            value: (watch('target_level') as LEVEL) || LEVEL.B2,
                                        }),
                                )}
                                defaultValue={LEVEL.A1}
                                disabled={isLoading}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <SingleSelect
                                fullWidth
                                name="target_level"
                                control={control}
                                errors={errors}
                                label={t('survey.form.targetLevel.label')}
                                options={levelOptions}
                                defaultValue={LEVEL.B2}
                                disabled={isLoading}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <SingleSelect
                                fullWidth
                                name="english_experience"
                                label={t('survey.form.englishLearningExperience.label')}
                                control={control}
                                errors={errors}
                                options={englishExperienceOptions()}
                                defaultValue={ENGLISH_EXPERIENCE.Beginner}
                                disabled={isLoading}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <SingleSelect
                                fullWidth
                                name="daily_commitment"
                                label={t('survey.form.dailyLearningCommitment.label')}
                                control={control}
                                errors={errors}
                                options={dailyCommitmentOptions()}
                                defaultValue={DAILY_COMMITMENT.Casual}
                                disabled={isLoading}
                            />
                        </Grid>
                    </Grid>
                );

            case 2:
                return (
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Typography
                                variant="h6"
                                gutterBottom
                                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                            >
                                <Settings /> {t('survey.titleStep2')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                {t('survey.descriptionStep2')}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={12}>
                            <SingleSelect
                                fullWidth
                                name="learning_goal"
                                label={t('survey.form.learningGoal.label')}
                                control={control}
                                errors={errors}
                                options={learningGoalOptions()}
                                defaultValue={LEARNING_GOAL.Ielts}
                                disabled={isLoading}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12}>
                            <SingleSelect
                                fullWidth
                                name="learning_style"
                                label={t('survey.form.learningStyle.label')}
                                control={control}
                                errors={errors}
                                options={learningStyleOptions()}
                                defaultValue={LEARNING_STYLE.Visual}
                                disabled={isLoading}
                            />
                        </Grid>
                        {user && (
                            <Grid item xs={12}>
                                <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                                    {t('survey.notificationPreferences')}
                                </Typography>
                                <CheckboxLabel
                                    name="study_reminders"
                                    control={control}
                                    label={t('survey.form.studyReminders.label')}
                                />
                                <CheckboxLabel
                                    name="achievement_notifications"
                                    control={control}
                                    label={t('survey.form.achievementNotifications.label')}
                                />
                            </Grid>
                        )}
                    </Grid>
                );

            default:
                return null;
        }
    };
    return (
        <Fragment>
            {/* Header */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Typography variant="h4" fontWeight="bold" color="primary" gutterBottom>
                    {t('survey.title')}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    {t('survey.description')}
                </Typography>
            </Box>

            {/* Stepper */}
            <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
                {steps().map((label) => (
                    <Step key={label}>
                        <StepLabel>{label}</StepLabel>
                    </Step>
                ))}
            </Stepper>

            {/* Form Content */}
            <Box component="form">
                <AnimatePresence mode="wait">
                    <motion.div
                        key={activeStep}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.3 }}
                    >
                        {renderStepContent(activeStep)}
                    </motion.div>
                </AnimatePresence>

                {/* Navigation Buttons */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                    <Button onClick={handleBack} disabled={activeStep === 0 || isLoading} startIcon={<ArrowBack />}>
                        {t('common.back')}
                    </Button>

                    {activeStep === steps().length - 1 ? (
                        <Button variant="contained" disabled={isLoading} sx={{ minWidth: 120 }} onClick={submit}>
                            {isLoading ? <CircularProgress size={24} color="inherit" /> : t('survey.complete')}
                        </Button>
                    ) : (
                        <Button
                            onClick={handleNext}
                            variant="contained"
                            disabled={isLoading}
                            endIcon={<ArrowForward />}
                        >
                            {t('common.next')}
                        </Button>
                    )}
                </Box>
            </Box>
        </Fragment>
    );
};

export default SurveyUser;
